import { CircularProgress, Stack, TextField } from '@mui/material';
import { AxiosError } from 'axios';
import {
  FC,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AuthenticationContext,
  notificationsActions,
  useAppDispatch,
  useUpdateAssociate,
} from '@vs-online-only-app/state';
import SmallButton from '../../SmallButton';

type PropertyName = 'firstName' | 'lastName' | 'email' | 'employeeId';
type InputType = 'text' | 'email';
type Props = {
  propertyName: PropertyName;
  label: string;
  type: InputType;
  onFocus: () => void;
  abort: boolean;
};
const EditProfileValue: FC<Props> = ({
  propertyName,
  label,
  type,
  onFocus,
  abort,
}) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useContext(AuthenticationContext);
  const [isEditModeOn, setEditModeOn] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const updateAssociate = useUpdateAssociate();
  const [propertyValue, setPropertyValue] = useState<string | undefined>('');

  useEffect(() => {
    if (!user) return;
    setPropertyValue(user[propertyName]);
  }, [propertyName, user]);

  useEffect(() => {
    if (!abort || !user) return;
    setEditModeOn(false);
    setPropertyValue(user[propertyName]);
    setErrorMessage('');
  }, [abort, propertyName, user]);

  const handleFieldFocus = useCallback(() => {
    setEditModeOn(true);
    onFocus();
  }, [onFocus]);

  const handleFieldChange = useCallback(() => {
    if (!inputRef.current) return;

    const value = inputRef.current.value.trim();
    if (!value) {
      setErrorMessage('missing value');
    } else {
      setErrorMessage('');
    }
    setPropertyValue(value);
  }, []);

  const handleFieldBlur = useCallback(() => {
    if (!inputRef.current || !user) return;

    const value = inputRef.current.value.trim();
    if (value === user[propertyName]) {
      setEditModeOn(false);
    }
  }, [propertyName, user]);

  const onSave = useCallback(async () => {
    if (!inputRef.current) return;
    const value = inputRef.current.value.trim();
    if (!value || !user || value === user[propertyName]) return;

    setIsBusy(true);
    try {
      await updateAssociate.mutateAsync({ [propertyName]: value });
      setEditModeOn(false);
      dispatch(
        notificationsActions.setCurrentNotification({
          message: `${label} has been updated.`,
          severity: 'success',
        }),
      );
    } catch (e) {
      if (e instanceof AxiosError) {
        const err = (e as AxiosError<{ message: string }>).response?.data
          ?.message;
        dispatch(
          notificationsActions.setCurrentNotification({
            message:
              typeof err === 'string' ? err : `Could not update ${label}`,
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          notificationsActions.setCurrentNotification({
            message: (e as Error).message,
            severity: 'error',
          }),
        );
      }
    }
    setIsBusy(false);
  }, [user, propertyName, updateAssociate, dispatch, label]);

  const onCancel = useCallback(() => {
    if (!user) return;
    setPropertyValue(user[propertyName]);

    setEditModeOn(false);
    setErrorMessage('');
  }, [propertyName, user]);

  const isSaveButtonDisabled = useMemo(() => {
    if (!user) return;
    return user[propertyName] === propertyValue;
  }, [user, propertyName, propertyValue]);

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave();
  };

  return (
    <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
      <Stack direction="column" gap={2}>
        <TextField
          type={type}
          inputRef={inputRef}
          label={`${label}${errorMessage ? ` - ${errorMessage}` : ''}`}
          variant="outlined"
          fullWidth
          error={!!errorMessage}
          disabled={isBusy}
          onFocus={() => handleFieldFocus()}
          onChange={() => handleFieldChange()}
          onBlur={() => handleFieldBlur()}
          value={propertyValue}
        />
        {isEditModeOn && (
          <Stack direction="row" justifyContent="right">
            <SmallButton disabled={isBusy} onClick={onCancel}>
              Cancel
            </SmallButton>
            <SmallButton
              disabled={!!errorMessage || isSaveButtonDisabled || isBusy}
              variant="contained"
              onClick={onSave}
            >
              {isBusy ? <CircularProgress color="inherit" size={20} /> : 'Save'}
            </SmallButton>
          </Stack>
        )}
      </Stack>
    </form>
  );
};

export default EditProfileValue;
